// ── Types (re-exported so consumers can import from one place) ────────────────
import type * as types from "./types";

// ── Demo Client ───────────────────────────────────────────────────────────────

const ok = <T>(data: T): Promise<T> => Promise.resolve(data);

export default class DemoQuikTripClient {
  private loggedIn = true;

  // ── Auth ──────────────────────────────────────────────────────────────────

  async login(_userName: string, _password: string): Promise<string> {
    this.loggedIn = true;
    return "demo-token-abc123";
  }

  isLoggedIn(): boolean {
    return this.loggedIn;
  }

  logout(): void {
    this.loggedIn = false;
  }

  // ── Employee ──────────────────────────────────────────────────────────────

  getEmployeeInfo(): Promise<types.EmployeeInfo> {
    return ok({
      emp_ID: 50012345,
      name: "DOE, JOHN A",
      division: "81",
      base_Store: "1047",
      job_Type_Desc: "PT Store Clerk",
      job_Type: "143",
      permissions: [
        "PayrollPage",
        "NotificationsPage",
        "WorkSchedulePage",
        "TimeOffPage",
        "StoresPage",
        "GoToRegister",
        "ExtraShiftPage",
      ],
      storeAddress: "104 E Crossover Dr",
      storePhone: "(864) 555-1203",
      extraShift: false,
      unreadNotifications: 0,
    });
  }

  // ── Pay Stubs ─────────────────────────────────────────────────────────────

  getAllPayStubs(): Promise<types.PayStubPeriod[]> {
    return ok([
      {
        weekEndDate: "05/01/2026 00:00:00",
        fullStubs: [
          {
            checkId: 1,
            employeeId: 50012345,
            netEarnings: 439.26,
            grossEarnings: 500.33,
            grossTaxableEarnings: 500.33,
            grossDeductions: 61.07,
            totalHours: 31.25,
            grossTaxableYearToDateEarnings: 9847.22,
            grossYearToDateEarnings: 9847.22,
            earnings: [
              {
                description: "Regular",
                hours: 31.25,
                rate: 14.5,
                gross: 453.13,
                yearToDate: 6923.41,
              },
              {
                description: "NonFlex Bon",
                hours: 0.0,
                rate: 0.0,
                gross: 47.2,
                yearToDate: 1734.18,
              },
            ],
            deductions: [
              { description: "Gross Pay", amount: 500.33, yearToDate: 9847.22 },
              { description: "Federal Tax", amount: 12.8, yearToDate: 352.17 },
              { description: "Fica Tax", amount: 31.02, yearToDate: 604.83 },
              { description: "Medicare Tax", amount: 7.25, yearToDate: 141.39 },
              { description: "Str Uniform", amount: 0.0, yearToDate: 16.0 },
              { description: "United Way", amount: 10.0, yearToDate: 200.0 },
            ],
            depositInfo: {
              disbursmentType: "Direct Deposit",
              depositNumber: 20260430,
              route: "811047",
              periodEnd: "04/24/2026 00:00:00",
              checkDate: "04/29/2026 00:00:00",
              accountNumber: "XXXXXXXX1234",
              netDeposit: "439.26",
            },
            employeeInfo: {
              employeeId: 50012345,
              jobCode: 143,
              jobStep: "600",
              maritalStatus: "J",
              name: "DOE, JOHN A",
              address: "456 Elm St Anytown ST 00000",
              federalExemtions: 0,
              additionalWithholdings: 0.0,
            },
            federalInfo: {
              federalGrossWages: 9346.89,
              taxExludableWages: 0.0,
              federalTaxableWages: 9346.89,
            },
            timeOffInfo: {
              vacationBeginingBalance: 0.0,
              vacationUsed: 0.0,
              vacationAvailable: 0.0,
              sickBeginingBalance: 0.0,
              sickTaken: 0.0,
              sickAvailable: 0.0,
              partTimeBonusHours: 1203.5,
              partTimeBonusDollars: 0.0,
              averageHoursWorked: 28.74,
              sinceDate: "03/06/2026 00:00:00",
              allIn: 23.145,
            },
          },
        ],
        displayStubs: [
          {
            hours: 31.25,
            gross: 500.33,
            deductions: 61.07,
            net: 439.26,
            tax: 51.07,
            fourZeroOneK: 0.0,
            other: 10.0,
          },
        ],
      },
      {
        weekEndDate: "04/24/2026 00:00:00",
        fullStubs: [
          {
            checkId: 2,
            employeeId: 50012345,
            netEarnings: 269.08,
            grossEarnings: 308.05,
            grossTaxableEarnings: 308.05,
            grossDeductions: 38.97,
            totalHours: 18.5,
            grossTaxableYearToDateEarnings: 9346.89,
            grossYearToDateEarnings: 9346.89,
            earnings: [
              {
                description: "Regular",
                hours: 18.5,
                rate: 14.5,
                gross: 268.25,
                yearToDate: 6470.28,
              },
              {
                description: "NonFlex Bon",
                hours: 0.0,
                rate: 0.0,
                gross: 39.8,
                yearToDate: 1686.98,
              },
            ],
            deductions: [
              { description: "Gross Pay", amount: 308.05, yearToDate: 9346.89 },
              { description: "Federal Tax", amount: 5.4, yearToDate: 339.37 },
              { description: "Fica Tax", amount: 19.1, yearToDate: 573.81 },
              { description: "Medicare Tax", amount: 4.47, yearToDate: 134.14 },
              { description: "Str Uniform", amount: 0.0, yearToDate: 16.0 },
              { description: "United Way", amount: 10.0, yearToDate: 190.0 },
            ],
            depositInfo: {
              disbursmentType: "Direct Deposit",
              depositNumber: 20260423,
              route: "811047",
              periodEnd: "04/17/2026 00:00:00",
              checkDate: "04/22/2026 00:00:00",
              accountNumber: "XXXXXXXX1234",
              netDeposit: "269.08",
            },
            employeeInfo: {
              employeeId: 50012345,
              jobCode: 143,
              jobStep: "600",
              maritalStatus: "J",
              name: "DOE, JOHN A",
              address: "456 Elm St Anytown ST 00000",
              federalExemtions: 0,
              additionalWithholdings: 0.0,
            },
            federalInfo: {
              federalGrossWages: 8846.56,
              taxExludableWages: 0.0,
              federalTaxableWages: 8846.56,
            },
            timeOffInfo: {
              vacationBeginingBalance: 0.0,
              vacationUsed: 0.0,
              vacationAvailable: 0.0,
              sickBeginingBalance: 0.0,
              sickTaken: 0.0,
              sickAvailable: 0.0,
              partTimeBonusHours: 1171.75,
              partTimeBonusDollars: 0.0,
              averageHoursWorked: 25.32,
              sinceDate: "03/06/2026 00:00:00",
              allIn: 20.614,
            },
          },
        ],
        displayStubs: [
          {
            hours: 18.5,
            gross: 308.05,
            deductions: 38.97,
            net: 269.08,
            tax: 28.97,
            fourZeroOneK: 0.0,
            other: 10.0,
          },
        ],
      },
    ]);
  }

  // ── Work Schedule ─────────────────────────────────────────────────────────

  getWorkSchedule(): Promise<types.WorkSchedule> {
    return ok({
      current: [
        {
          dayOfWeek: 1,
          dayOfWeekName: "Mon",
          storeNum: "1047",
          lat: "35.33397391",
          long: "-80.71839559",
          phoneNumber: "(864) 555-1234",
          shifts: [
            {
              shiftId: 87380624,
              displayTime: "03:00 PM - 11:00 PM",
              shiftType: 1,
              shiftTypeName: "Regular",
              doubled: false,
            },
          ],
          displayDate: "Mon 05/04",
        },
        {
          dayOfWeek: 2,
          dayOfWeekName: "Tue",
          storeNum: "1047",
          lat: "35.33397391",
          long: "-80.71839559",
          phoneNumber: "(864) 555-1234",
          shifts: [],
          displayDate: "Tue 05/05",
        },
        {
          dayOfWeek: 3,
          dayOfWeekName: "Wed",
          storeNum: "1047",
          lat: "35.33397391",
          long: "-80.71839559",
          phoneNumber: "(864) 555-1234",
          shifts: [
            {
              shiftId: 87380625,
              displayTime: "07:00 AM - 03:00 PM",
              shiftType: 1,
              shiftTypeName: "Regular",
              doubled: false,
            },
          ],
          displayDate: "Wed 05/06",
        },
        {
          dayOfWeek: 4,
          dayOfWeekName: "Thu",
          storeNum: "1047",
          lat: "35.33397391",
          long: "-80.71839559",
          phoneNumber: "(864) 555-1234",
          shifts: [],
          displayDate: "Thu 05/07",
        },
        {
          dayOfWeek: 5,
          dayOfWeekName: "Fri",
          storeNum: "1047",
          lat: "35.33397391",
          long: "-80.71839559",
          phoneNumber: "(864) 555-1234",
          shifts: [
            {
              shiftId: 87380626,
              displayTime: "03:00 PM - 11:00 PM",
              shiftType: 1,
              shiftTypeName: "Regular",
              doubled: false,
            },
          ],
          displayDate: "Fri 05/08",
        },
        {
          dayOfWeek: 6,
          dayOfWeekName: "Sat",
          storeNum: "1047",
          lat: "35.33397391",
          long: "-80.71839559",
          phoneNumber: "(864) 555-1234",
          shifts: [
            {
              shiftId: 87380627,
              displayTime: "07:00 AM - 03:00 PM",
              shiftType: 1,
              shiftTypeName: "Regular",
              doubled: false,
            },
          ],
          displayDate: "Sat 05/09",
        },
        {
          dayOfWeek: 0,
          dayOfWeekName: "Sun",
          storeNum: "1047",
          lat: "35.33397391",
          long: "-80.71839559",
          phoneNumber: "(864) 555-1234",
          shifts: [],
          displayDate: "Sun 05/10",
        },
      ],
      future: [
        {
          dayOfWeek: 1,
          dayOfWeekName: "Mon",
          storeNum: "1047",
          lat: "35.33397391",
          long: "-80.71839559",
          phoneNumber: "(864) 555-1234",
          shifts: [
            {
              shiftId: 87380630,
              displayTime: "03:00 PM - 11:00 PM",
              shiftType: 1,
              shiftTypeName: "Regular",
              doubled: false,
            },
          ],
          displayDate: "Mon 05/11",
        },
        {
          dayOfWeek: 2,
          dayOfWeekName: "Tue",
          storeNum: "1047",
          lat: "35.33397391",
          long: "-80.71839559",
          phoneNumber: "(864) 555-1234",
          shifts: [],
          displayDate: "Tue 05/12",
        },
        {
          dayOfWeek: 3,
          dayOfWeekName: "Wed",
          storeNum: "1047",
          lat: "35.33397391",
          long: "-80.71839559",
          phoneNumber: "(864) 555-1234",
          shifts: [
            {
              shiftId: 87380631,
              displayTime: "07:00 AM - 03:00 PM",
              shiftType: 1,
              shiftTypeName: "Regular",
              doubled: false,
            },
          ],
          displayDate: "Wed 05/13",
        },
        {
          dayOfWeek: 4,
          dayOfWeekName: "Thu",
          storeNum: "1047",
          lat: "35.33397391",
          long: "-80.71839559",
          phoneNumber: "(864) 555-1234",
          shifts: [],
          displayDate: "Thu 05/14",
        },
        {
          dayOfWeek: 5,
          dayOfWeekName: "Fri",
          storeNum: "1047",
          lat: "35.33397391",
          long: "-80.71839559",
          phoneNumber: "(864) 555-1234",
          shifts: [],
          displayDate: "Fri 05/15",
        },
        {
          dayOfWeek: 6,
          dayOfWeekName: "Sat",
          storeNum: "1047",
          lat: "35.33397391",
          long: "-80.71839559",
          phoneNumber: "(864) 555-1234",
          shifts: [
            {
              shiftId: 87380632,
              displayTime: "11:00 PM - 07:00 AM",
              shiftType: 1,
              shiftTypeName: "Regular",
              doubled: false,
            },
          ],
          displayDate: "Sat 05/16",
        },
        {
          dayOfWeek: 0,
          dayOfWeekName: "Sun",
          storeNum: "1047",
          lat: "35.33397391",
          long: "-80.71839559",
          phoneNumber: "(864) 555-1234",
          shifts: [],
          displayDate: "Sun 05/17",
        },
      ],
    });
  }

  // ── Time Off ──────────────────────────────────────────────────────────────

  getAllTimeOff(): Promise<types.TimeOffData> {
    return ok({
      matrix: [
        {
          employeeId: 50012345,
          reasonCodeId: 6,
          explanationCodeId: 84,
          matrixGroupId: null,
          isGroupParent: false,
          reason: "Personal Time/Formal Event",
          beginningBalance: 5.0,
          taken: 4.0,
          available: 1.0,
          future: 0.0,
          futureAwaitingApproval: 0.0,
          futureApproved: 0.0,
          futureStaffed: 0.0,
          futureNotStaffed: 0.0,
          amountTypeId: 2,
          groupMembers: null,
        },
      ],
      futureTimeOff: [],
      historyTimeOff: [
        {
          store: 1047,
          requestDate: "04/17/2026 00:00:00",
          startTime: "04/17/2026 00:00:00",
          endTime: "04/17/2026 00:00:00",
          explanationCodeId: 87,
          explanationCodeDesc: "Sick - Normal",
          requestPercentage: 1.0,
          statusId: 3,
          statusDesc: "Approved",
        },
      ],
    });
  }

  getRequestPageData(_id: string): Promise<unknown> {
    return ok({
      explanationCodes: [
        { id: 84, description: "Personal Time/Formal Event" },
        { id: 87, description: "Sick - Normal" },
      ],
    });
  }

  createTimeOffRequest(_request: types.CreateTimeOffRequest): Promise<unknown> {
    return ok({ success: true });
  }

  // ── Extra Shifts ──────────────────────────────────────────────────────────

  getExtraShiftsByWeek(): Promise<types.ExtraShiftWeek[]> {
    return ok([
      {
        startOfWeek: "2026-04-27",
        extraShifts: [
          {
            postedByEmployeeId: 97948,
            extraShiftId: 3459410,
            workScheduleId: 0,
            storeNumber: 1032,
            status: 0,
            startTime: "04/29/2026 05:00:00",
            endTime: "04/29/2026 08:00:00",
            displayTime: "05:00 AM - 08:00 AM",
            latitude: "35.33397391",
            longitude: "-80.71839559",
            distance: 52.0,
            isTimeOff: false,
            reasonForRemoval: null,
            isER: false,
          },
        ],
      },
    ]);
  }

  getClerkExtraShiftSchedule(): Promise<types.ExtraShift[]> {
    return ok([]);
  }

  createExtraShift(_request: types.CreateExtraShiftRequest): Promise<unknown> {
    return ok({ success: true });
  }

  acceptExtraShift(_extraShiftId: string): Promise<unknown> {
    return ok({ success: true });
  }

  acceptTimeOffExtraShift(_clientDateTime: string): Promise<unknown> {
    return ok({ success: true });
  }

  cancelExtraShift(_extraShiftId: string): Promise<unknown> {
    return ok({ success: true });
  }

  // ── Notifications ─────────────────────────────────────────────────────────

  getNotificationTypes(): Promise<types.NotificationType[]> {
    return ok([
      { id: 1, pushType: "Extra Shift Accepted" },
      { id: 2, pushType: "Time Off Approved" },
      { id: 3, pushType: "Employee Birthday" },
      { id: 4, pushType: "Employee Anniversary" },
    ]);
  }

  getSentUserNotifications(): Promise<unknown[]> {
    return ok([]);
  }

  // ── Stores ────────────────────────────────────────────────────────────────

  getDivisionStores(_divisionId: string): Promise<types.Store[]> {
    return ok([
      {
        storeNumber: 1002,
        intersection: "NEC Wilkinson Blvd & Todd Rd",
        address: "7500 Wilkinson Blvd",
        city: "Charlotte",
        state: "NC",
        zip: "28214",
        latitude: "35.239792",
        longitude: "-80.96662462",
        phone: "(704) 555-5063",
      },
      {
        storeNumber: 1047,
        intersection: "SEC Crossover Dr & Stadium Rd",
        address: "104 E Crossover Dr",
        city: "Greenville",
        state: "SC",
        zip: "29615",
        latitude: "35.33397391",
        longitude: "-80.71839559",
        phone: "(864) 555-1203",
      },
      {
        storeNumber: 1032,
        intersection: "NWC Independence Blvd & Monroe Rd",
        address: "4301 Monroe Rd",
        city: "Charlotte",
        state: "NC",
        zip: "28205",
        latitude: "35.20184",
        longitude: "-80.78912",
        phone: "(704) 555-8821",
      },
    ]);
  }
}
